"use client"

import { useState, useEffect, useRef } from "react"
import { Search, Wifi, WifiOff, Clock, X, Keyboard, RefreshCw, QrCode } from "lucide-react"
import DynamicTable from "./DynamicTable"
import KioskPrintModal from "./KioskPrintModal"
import LoadingBar from "./LoadingBar"
import Toast from "./Toast"
import VirtualKeyboard from "./VirtualKeyboard"
import { useDynamicUsers } from "../hooks/useDynamicUsers"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { useToast } from "../hooks/useToast"
import { useQueueManager } from "../hooks/useQueueManager"
import { useTourGuide } from "../hooks/useTourGuide"
import { setApiOperationCallback } from "../services/api"
import type { DynamicUser } from "../types/dynamic"

// Componente de animación de nodos networking
const NetworkingNodes = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <svg className="w-full h-full opacity-10">
        {/* Nodos */}
        {Array.from({ length: 12 }).map((_, i) => (
          <g key={i}>
            <circle
              cx={`${20 + (i * 8) % 80}%`}
              cy={`${15 + (i * 7) % 70}%`}
              r="2"
              fill="#040526"
              className="animate-pulse"
              style={{
                animationDelay: `${i * 0.5}s`,
                animationDuration: '3s'
              }}
            />
            {/* Líneas conectoras */}
            {i < 11 && (
              <line
                x1={`${20 + (i * 8) % 80}%`}
                y1={`${15 + (i * 7) % 70}%`}
                x2={`${20 + ((i + 1) * 8) % 80}%`}
                y2={`${15 + ((i + 1) * 7) % 70}%`}
                stroke="#040526"
                strokeWidth="0.5"
                opacity="0.3"
                className="animate-pulse"
                style={{
                  animationDelay: `${i * 0.7}s`,
                  animationDuration: '4s'
                }}
              />
            )}
          </g>
        ))}
      </svg>
    </div>
  )
}

interface QRScanAnimationProps {
  setShowVirtualKeyboard: (show: boolean) => void
  searchInputRef: React.RefObject<HTMLInputElement>
}

// Componente de animación QR
const QRScanAnimation = ({
  setShowVirtualKeyboard,
  searchInputRef
}: QRScanAnimationProps) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-8 py-12">
      {/* Animación de fondo gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 animate-pulse opacity-50"></div>

      {/* Scanner QR animado */}
      <div className="relative z-10">
        <div className="relative w-32 h-32 border-4 border-blue-500 rounded-xl bg-white/80 backdrop-blur-sm shadow-2xl">
          {/* Esquinas del scanner */}
          <div className="absolute -top-2 -left-2 w-6 h-6 border-t-4 border-l-4 border-blue-600"></div>
          <div className="absolute -top-2 -right-2 w-6 h-6 border-t-4 border-r-4 border-blue-600"></div>
          <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-4 border-l-4 border-blue-600"></div>
          <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-4 border-r-4 border-blue-600"></div>

          {/* QR Code simulado */}
          <div className="absolute inset-4 grid grid-cols-6 gap-1">
            {Array.from({ length: 36 }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-sm ${
                  Math.random() > 0.5 ? 'bg-gray-800' : 'bg-gray-200'
                } animate-pulse`}
                style={{
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: '2s'
                }}
              />
            ))}
          </div>

          {/* Línea de escaneado */}
          <div className="absolute inset-x-2 h-1 bg-red-500 opacity-70 animate-bounce"></div>
        </div>

        {/* Icono QR */}
        <div className="absolute -top-4 -right-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
          <QrCode className="h-4 w-4 text-white" />
        </div>
      </div>

      {/* Texto sin animaciones */}
      <div className="relative z-10 text-center space-y-6">
        <h2 className="text-4xl font-bold text-white drop-shadow-lg">
          ¡Bienvenido al Módulo de Autoregistro!
        </h2>

          <div className="space-y-4">
            <div className="text-center text-xl text-white font-medium">
              Acerca tu código QR al lector
            </div>

            <button
              onClick={() => {
                setShowVirtualKeyboard(true)
                if (searchInputRef.current) {
                  searchInputRef.current.focus()
                }
              }}
              className="flex items-center justify-center space-x-4 text-xl text-white bg-black/40 backdrop-blur-sm rounded-xl px-6 py-3 border border-white/20 hover:bg-black/50 transition-colors duration-200 mx-auto"
            >
              <div className="w-3 h-3 bg-green-400 rounded-full shadow-lg"></div>
              <span className="font-medium">Busca tu nombre en el evento</span>
            </button>
          </div>
      </div>
    </div>
  )
}

const KioskView = () => {
  const isOnline = useNetworkStatus()

  // Hook de usuarios dinámicos
  const {
    users: dynamicUsers,
    schema: dynamicSchema,
    config: dynamicConfig,
    loading: dynamicLoading,
    error: dynamicError,
    refreshData: refreshDynamicData,
    cancelCurrentFetch: cancelDynamicFetch,
    setApiOperationActive: setDynamicApiOperationActive,
    getFieldValue,
    formatFieldValue,
    filterUsers,
    updateUserOptimistically,
  } = useDynamicUsers()

  const {
    queueCount,
    isProcessing,
    lastSync,
    error: queueError,
  } = useQueueManager(() => {
    refreshDynamicData()
  }, () => {})

  const { toasts, showToast, removeToast } = useToast()
  const { isActive: isTourActive } = useTourGuide()

  // Estados de búsqueda
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearchLocked, setIsSearchLocked] = useState(false)
  const [lockedSearchTerm, setLockedSearchTerm] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [windowInFocus, setWindowInFocus] = useState(true)

  // Estados específicos del kiosco
  const [selectedUser, setSelectedUser] = useState<DynamicUser | null>(null)
  const [showPrintModal, setShowPrintModal] = useState(false)

  // Estados del teclado virtual
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false)

  // Configure API operation tracking
  useEffect(() => {
    setApiOperationCallback(setDynamicApiOperationActive)
  }, [setDynamicApiOperationActive])

  // Track window focus
  useEffect(() => {
    const handleFocus = () => setWindowInFocus(true)
    const handleBlur = () => setWindowInFocus(false)

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  // FUNCIONES DE BÚSQUEDA
  const lockSearch = () => {
    if (searchTerm.trim()) {
      setLockedSearchTerm(searchTerm.trim())
      setIsSearchLocked(true)
      setSearchTerm("")
      if (searchInputRef.current) {
        searchInputRef.current.blur()
      }
    }
  }



  const clearSearch = () => {
    setSearchTerm("")
    setIsSearchLocked(false)
    setLockedSearchTerm("")
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }

  // Escuchar evento para limpiar búsqueda
  useEffect(() => {
    const handleClearSearch = () => {
      clearSearch()
    }

    window.addEventListener('clear-kiosk-search', handleClearSearch)
    return () => {
      window.removeEventListener('clear-kiosk-search', handleClearSearch)
    }
  }, [])

  const getEffectiveSearchTerm = () => {
    return isSearchLocked ? lockedSearchTerm : searchTerm
  }

  // SISTEMA DE BÚSQUEDA GLOBAL - MEJORADO PARA KIOSCO
  useEffect(() => {
    const handleGlobalKeydown = (event: KeyboardEvent) => {
      // Condiciones para NO interceptar el teclado:

      // 1. Si la ventana no está en foco
      if (!windowInFocus) return

      // 2. Si se presionaron teclas especiales (Ctrl, Alt, Meta, etc.)
      if (event.ctrlKey || event.altKey || event.metaKey) return

      // 3. Si es una tecla especial
      const specialKeys = [
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'Escape', 'Tab',
        'Home', 'End', 'PageUp', 'PageDown', 'F1', 'F2', 'F3', 'F4', 'F5',
        'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12', 'Shift', 'Control', 'Alt', 'Meta'
      ]
      if (specialKeys.includes(event.key)) return

      // 4. Si el tour guide está activo
      if (isTourActive) return

      // 5. Si hay algún modal abierto (solo el modal de impresión debería bloquear)
      if (showPrintModal) return

      // 6. Si el foco está en otro input, textarea o elemento editable (EXCEPTO nuestro search input)
      const activeElement = document.activeElement
      if (activeElement && activeElement !== searchInputRef.current) {
        if (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          (activeElement as HTMLElement).isContentEditable
        ) {
          return
        }
      }

      // 7. Manejar Enter para bloquear búsqueda
      if (event.key === 'Enter') {
        if (activeElement === searchInputRef.current && searchTerm.trim()) {
          setLockedSearchTerm(searchTerm.trim())
          setIsSearchLocked(true)
          setSearchTerm("")
          if (searchInputRef.current) {
            searchInputRef.current.blur()
          }
          event.preventDefault()
        }
        return
      }

      // 8. Manejar Delete/Backspace para borrar en el campo de búsqueda
      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (searchInputRef.current) {
          searchInputRef.current.focus()

          if (event.key === 'Backspace') {
            setSearchTerm(prev => prev.slice(0, -1))
          } else if (event.key === 'Delete') {
            setSearchTerm('')
          }

          event.preventDefault()
        }
        return
      }

      // 9. Si llegamos aquí, es una tecla normal (letra, número, etc.)
      // Enfocar el input de búsqueda y agregar el carácter
      if (searchInputRef.current && event.key.length === 1) {
        // Si la búsqueda está bloqueada y escribimos algo nuevo, desbloquear primero
        if (isSearchLocked) {
          setIsSearchLocked(false)
          setLockedSearchTerm("")
          setSearchTerm("")
        }

        // Enfocar el input
        searchInputRef.current.focus()

        // Si es una letra/número/símbolo válido, agregarlo al término de búsqueda
        if (event.key.match(/^[a-zA-Z0-9\sáéíóúüñÁÉÍÓÚÜÑ@._-]$/)) {
          setSearchTerm(prev => prev + event.key)
          // Prevenir que el carácter se escriba dos veces
          event.preventDefault()
        }
      }
    }

    // Agregar el event listener
    document.addEventListener('keydown', handleGlobalKeydown, { capture: true })

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleGlobalKeydown, { capture: true })
    }
  }, [showPrintModal, isTourActive, windowInFocus, searchTerm, isSearchLocked])

  // Funciones del teclado virtual
  const handleVirtualKeyboardInput = (input: string) => {
    if (input === '{bksp}') {
      setSearchTerm(prev => prev.slice(0, -1))
    } else if (input === '{space}') {
      setSearchTerm(prev => prev + ' ')
    } else if (input === '{enter}') {
      if (searchTerm.trim()) {
        lockSearch()
      }
      setShowVirtualKeyboard(false)
    } else if (input.length === 1) {
      setSearchTerm(prev => prev + input)
    }
  }

  const handleInputTouch = () => {
    setShowVirtualKeyboard(true)
  }

  const handleToggleKeyboard = () => {
    setShowVirtualKeyboard(!showVirtualKeyboard)
  }

  // Show toast notifications for queue errors
  useEffect(() => {
    if (queueError) {
      showToast("error", `Error en sincronización: ${queueError}`)
    }
  }, [queueError, showToast])

  const handlePrintUser = (user: DynamicUser) => {
    setSelectedUser(user)
    setShowPrintModal(true)
    setShowVirtualKeyboard(false)
  }

  const handlePrintSuccess = (updatedUser: DynamicUser) => {
    updateUserOptimistically(updatedUser)
    setShowPrintModal(false)
    setSelectedUser(null)
    showToast("success", "¡Gafete impreso correctamente!")
  }

  const getSyncStatus = () => {
    if (isProcessing) return "Sincronizando..."
    if (queueCount > 0) return `${queueCount} pendientes`
    if (lastSync) return "Sincronizado"
    return "Sin sincronizar"
  }

  const getSyncStatusGlassColor = () => {
    if (isProcessing) return "bg-yellow-500/20 border-yellow-300/30 text-yellow-800"
    if (queueCount > 0) return "bg-orange-500/20 border-orange-300/30 text-orange-800"
    if (lastSync) return "bg-green-500/20 border-green-300/30 text-green-800"
    return "bg-gray-500/20 border-gray-300/30 text-gray-800"
  }

  const formatTime = () => {
    return new Date().toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <div className="min-h-screen relative">
      {/* Fondo con imagen */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/fondo_modulo.png')`,
        }}
      ></div>
      {/* Overlay para mejor legibilidad */}
      <div className="fixed inset-0 bg-black/20"></div>

      {/* Animación de nodos networking */}
      <NetworkingNodes />

      {/* Loading bar */}
      <LoadingBar isLoading={dynamicLoading || isProcessing} />

      {/* Header con efecto glass */}
      <header id="navbar" className="relative z-10 bg-white/20 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-4">
                <img
                  src="/logoamerica-digital.png"
                  alt="America Digital Logo"
                  className="w-16 h-16 object-contain"
                />
                <div>
                  <h1 className="text-3xl font-bold text-white">Femecot 2026</h1>
                  <p className="text-lg text-blue-100 font-medium">Módulo de Autoregistro</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Sync Status */}
              <span className={`px-3 py-1 rounded-xl text-sm font-medium backdrop-blur-sm border transition-all duration-300 ${getSyncStatusGlassColor()}`}>
                {getSyncStatus()}
              </span>

              {/* Network Status */}
              <div className="flex items-center space-x-2">
                {isOnline ? (
                  <div className="flex items-center px-3 py-1 bg-blue-500/20 backdrop-blur-sm border border-blue-300/30 text-blue-100 rounded-xl text-sm font-medium">
                    <Wifi className="h-4 w-4 mr-1" />
                    En línea
                  </div>
                ) : (
                  <div className="flex items-center px-3 py-1 bg-red-500/20 backdrop-blur-sm border border-red-300/30 text-red-100 rounded-xl text-sm font-medium">
                    <WifiOff className="h-4 w-4 mr-1" />
                    Sin conexión
                  </div>
                )}
              </div>

              {/* Hora actual */}
              <div className="flex items-center px-3 py-1 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-xl text-sm font-medium">
                <Clock className="h-4 w-4 mr-1" />
                {formatTime()}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        {/* Search Bar con efecto glass */}
        <div className="flex justify-between items-center mb-6">
          <div id="search-bar" className="flex-1 max-w-md">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={
                  isSearchLocked
                    ? `Buscando: ${lockedSearchTerm}`
                    : "Buscar usuarios para imprimir gafete..."
                }
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  if (isSearchLocked && e.target.value) {
                    setIsSearchLocked(false)
                    setLockedSearchTerm("")
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    lockSearch()
                  }
                }}
                onTouchStart={handleInputTouch}
                className={`w-full pl-10 pr-20 py-3 bg-white/20 backdrop-blur-md border-2 border-white/30 text-white rounded-xl outline-none transition-all duration-300 placeholder-white/70 text-lg ${
                  isSearchLocked
                    ? "border-orange-400/50 focus:border-orange-500/70 focus:ring-2 focus:ring-orange-500/20 focus:bg-orange-500/10"
                    : "border-white/30 focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20 focus:bg-blue-500/10"
                }`}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-6 w-6 transition-colors duration-300 ${
                  isSearchLocked ? "text-orange-300" : "text-white/70"
                }`} />
              </div>

              {/* Botones del lado derecho */}
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1">
                {/* Botón del teclado virtual */}
                <button
                  onClick={handleToggleKeyboard}
                  className={`p-2 rounded-lg transition-all duration-200 backdrop-blur-sm border ${
                    showVirtualKeyboard
                      ? 'bg-blue-500/50 text-white shadow-lg border-blue-300/50'
                      : 'bg-white/20 text-white/70 hover:bg-white/30 border-white/30'
                  }`}
                  title={showVirtualKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                >
                  <Keyboard className="h-4 w-4" />
                </button>

                {/* Botón X para limpiar búsqueda */}
                {(searchTerm.length > 0 || isSearchLocked) && (
                  <button
                    onClick={clearSearch}
                    className="p-2 rounded-lg bg-white/20 backdrop-blur-sm border border-white/30 text-white/70 hover:bg-white/30 transition-all duration-200"
                    title="Limpiar búsqueda"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex space-x-3 ml-4">
            <button
              onClick={() => refreshDynamicData()}
              disabled={dynamicLoading}
              className="inline-flex items-center px-4 py-2 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 text-blue-100 text-sm font-medium rounded-xl hover:bg-blue-600/30 hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {dynamicLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Actualizar
            </button>
          </div>
        </div>

        {/* Teclado Virtual fijo en parte inferior */}
        {showVirtualKeyboard && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/10 backdrop-blur-md border-t border-white/20 p-4">
            <VirtualKeyboard
              onKeyPress={handleVirtualKeyboardInput}
              onClose={() => setShowVirtualKeyboard(false)}
              currentValue={searchTerm}
            />
          </div>
        )}

        {/* Instrucciones con animación QR */}
        {!getEffectiveSearchTerm() && (
          <div className="mb-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl overflow-hidden">
            <QRScanAnimation
              setShowVirtualKeyboard={setShowVirtualKeyboard}
              searchInputRef={searchInputRef}
            />
          </div>
        )}

        {/* Table con efecto glass - Solo mostrar si hay búsqueda */}
        {getEffectiveSearchTerm() && (
          <div id="table-container" className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl overflow-hidden">
            <DynamicTable
              users={dynamicUsers}
              schema={dynamicSchema}
              config={dynamicConfig}
              loading={dynamicLoading}
              error={dynamicError}
              showToast={showToast}
              searchTerm={getEffectiveSearchTerm()}
              cancelCurrentFetch={cancelDynamicFetch}
              getFieldValue={getFieldValue}
              formatFieldValue={formatFieldValue}
              filterUsers={filterUsers}
              onEditSuccess={() => {}} // No permitir edición en kiosco
              loadingStep="Cargando sistema dinámico..."
              kioskMode={true}
              onPrintUser={handlePrintUser}
            />
          </div>
        )}
      </main>

      {/* Modal de impresión */}
      {showPrintModal && selectedUser && dynamicSchema && (
        <KioskPrintModal
          user={selectedUser}
          schema={dynamicSchema}
          onClose={() => {
            setShowPrintModal(false)
            setSelectedUser(null)
          }}
          onPrintSuccess={handlePrintSuccess}
          showToast={showToast}
          onClearSearch={clearSearch}
        />
      )}

      {/* Toast notifications */}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          type={toast.type}
          message={toast.message}
          onClose={() => removeToast(toast.id)}
          index={index}
        />
      ))}
    </div>
  )
}

export default KioskView
